/**
 * Step definitions for the refinance wizard.
 *
 * WI-TV-W5-001 established the numbered step ordering and the navigation
 * contract (required vs optional/skippable). WI-TV-W5-002 replaces the
 * placeholder bodies for the refinance goal, property, estimated value, current
 * mortgage balance, and requested loan amount steps with the real controls and
 * validation, driven directly by the authoritative referral model
 * (`src/models/referral.ts`) and mirroring `src/form/validation.ts`.
 *
 * WI-TV-W5-003 delivered the credit, military, and borrower-contact steps;
 * WI-TV-W6-003 moved those purpose-neutral steps into the shared
 * `borrower-steps` module so the purchase journey asks them identically. They
 * are re-exported here unchanged, so the refinance flow, its copy, its
 * validation, and its mappings are untouched.
 *
 * Value keys are the authoritative `ReferralFormState` field names, so wizard
 * answers map into the existing payload builder without any new naming or
 * payload semantics.
 */
import {
  CASH_OUT_REFINANCE,
  MILITARY_COMPONENT_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  RATE_TERM_REFINANCE,
  REFINANCE_GOAL_OPTIONS,
  STATE_OPTIONS,
  VA_STREAMLINE_REFINANCE,
} from "../models/referral";
import { isValidStateCode } from "../form/validation";
import { createCoBorrowerSteps } from "./coborrower-steps";
import {
  bindWholeDollarMoneyInput,
  formatWholeDollarForDisplay,
  parseMoneyInput,
  WHOLE_DOLLAR_FORMAT_MESSAGE,
} from "./money";
import {
  bindRadioGroup,
  bindValueInputs,
  escapeHtml,
  radioGroupHtml,
  selectFieldHtml,
  textFieldHtml,
} from "./step-fields";
import {
  borrowerNameStep,
  borrowerPhoneStep,
  createBorrowerEmailStep,
  creditStep,
  MILITARY_ANSWER_KEY,
  MILITARY_COMPONENT_KEY,
  militaryStep,
} from "./borrower-steps";
import type { WizardFieldError, WizardStep, WizardValues } from "./wizard-machine";

/** Value keys captured by the implemented steps (authoritative model names). */
export const REFINANCE_TYPE_KEY = "refinanceType";
export const PROPERTY_STREET_KEY = "propertyStreetAddress";
export const PROPERTY_CITY_KEY = "propertyCity";
export const PROPERTY_STATE_KEY = "propertyState";
export const PROPERTY_ZIP_KEY = "propertyZipCode";
export const PROPERTY_TYPE_KEY = "propertyType";
export const ESTIMATED_VALUE_KEY = "estimatedValue";
export const CURRENT_LOAN_BALANCE_KEY = "currentLoanBalance";
export const LOAN_AMOUNT_KEY = "loanAmount";

/**
 * True when the answers establish a cash-out refinance, the only goal that asks
 * for a separate requested loan amount. Rate-and-term sends the current
 * mortgage balance as its loan amount, so it neither asks for nor retains one.
 */
export function isCashOutRefinance(values: Readonly<Record<string, string>>): boolean {
  return (values[REFINANCE_TYPE_KEY] ?? "").trim() === CASH_OUT_REFINANCE;
}

/** Maps a UI-only refinance goal to the wire value used downstream. */
export function toWireRefinanceType(goal: string): string {
  if (goal === VA_STREAMLINE_REFINANCE) return RATE_TERM_REFINANCE;
  return goal;
}

/**
 * The stated-credit, application-military, and primary-contact steps live in the
 * shared `borrower-steps` module, which both journeys use. They are re-exported
 * here so the refinance flow keeps its single import surface.
 */
export {
  CREDIT_SCORE_RANGE_KEY,
  EMAIL_KEY,
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  MILITARY_ANSWER_KEY,
  MILITARY_ANSWER_OPTIONS,
  MILITARY_COMPONENT_KEY,
  MILITARY_PROMPT,
  PHONE_KEY,
  borrowerNameStep,
  borrowerPhoneStep,
  creditStep,
  isValidEmailInput,
  isValidPhoneInput,
  militaryStep,
  phoneDigits,
} from "./borrower-steps";

/** A supplied property ZIP code must be exactly five digits. */
export const PROPERTY_ZIP_REGEX = /^\d{5}$/;

/** True when the selected refinance goal is the UI-only VA Streamline option. */
export function isVaStreamlineRefinance(values: Readonly<Record<string, string>>): boolean {
  return (values[REFINANCE_TYPE_KEY] ?? "").trim() === VA_STREAMLINE_REFINANCE;
}

/**
 * Step 1 — Refinance goal. A required single-select radio group over the
 * refinance goals: the two authoritative wire refinance types plus the UI-only
 * VA Streamline option. Continue is blocked until a goal is chosen, and the
 * chosen value is retained across backward navigation. This is the entry
 * differentiator that routes a borrower onto the refinance wizard.
 *
 * Selecting VA Streamline forces the military question later in the flow to be
 * required: the answer is automatically set to "yes" here so the borrower can
 * only proceed by picking a branch.
 */
export const refinanceGoalStep: WizardStep = {
  id: "refinance-goal",
  title: "Refinance goal",
  validate: (values: WizardValues): WizardFieldError[] => {
    const selected = values.get(REFINANCE_TYPE_KEY);
    const valid = REFINANCE_GOAL_OPTIONS.some((o) => o.value === selected);
    if (!valid) {
      return [
        {
          field: `${REFINANCE_TYPE_KEY}-${REFINANCE_GOAL_OPTIONS[0].value}`,
          message: "Select a refinance goal to continue.",
        },
      ];
    }
    return [];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    const selected = values.get(REFINANCE_TYPE_KEY);
    container.innerHTML = `
      <p class="wizard-step-desc" id="refinance-goal-desc">
        What would you like to do with your refinance?
      </p>
      <fieldset class="wizard-choice-group">
        <legend class="visually-hidden">Refinance goal</legend>
        ${REFINANCE_GOAL_OPTIONS.map((option, i) => {
          const id = `${REFINANCE_TYPE_KEY}-${option.value}`;
          return `
          <label class="wizard-choice" for="${id}">
            <input
              type="radio"
              id="${id}"
              name="${REFINANCE_TYPE_KEY}"
              value="${escapeHtml(option.value)}"
              ${selected === option.value ? "checked" : ""}
              ${i === 0 ? 'data-autofocus="true"' : ""}
            />
            <span class="wizard-choice-label">${escapeHtml(option.label)}</span>
          </label>`;
        }).join("")}
      </fieldset>`;

    container
      .querySelectorAll<HTMLInputElement>(`input[name="${REFINANCE_TYPE_KEY}"]`)
      .forEach((input) => {
        input.addEventListener("change", () => {
          if (!input.checked) return;
          values.set(REFINANCE_TYPE_KEY, input.value);
          // A goal that asks no separate requested amount must not keep the one
          // an earlier cash-out answer left behind: it would be invisible, and
          // the outgoing amount is derived from the current balance instead.
          if (input.value !== CASH_OUT_REFINANCE) values.set(LOAN_AMOUNT_KEY, "");
          // VA Streamline requires a military branch, so pre-assert "yes" now.
          // The dedicated VA Streamline military step is required and validates
          // the branch before allowing the borrower to continue.
          if (input.value === VA_STREAMLINE_REFINANCE) {
            values.set(MILITARY_ANSWER_KEY, "yes");
          } else if (
            values.get(MILITARY_COMPONENT_KEY) === "" &&
            values.get(MILITARY_ANSWER_KEY) === "yes"
          ) {
            // Leaving VA Streamline clears an auto-asserted military answer so a
            // stale required-branch answer does not survive a goal change.
            values.set(MILITARY_ANSWER_KEY, "");
          }
        });
      });
  },
};

/**
 * Step 2 — Property details. Manual address entry only: street, city, and ZIP
 * are optional and may be left blank (skipped within the step), while the
 * property state and property type are required, so the step as a whole can
 * never be bypassed. A supplied ZIP must be exactly five digits, matching the
 * authoritative `propertyZipCode` rule. State and type are constrained to the
 * authoritative `STATE_CODES` and `PROPERTY_TYPES` enum values.
 */
export const propertyDetailsStep: WizardStep = {
  id: "property-details",
  title: "Property details",
  validate: (values: WizardValues): WizardFieldError[] => {
    const errors: WizardFieldError[] = [];

    // Errors are produced in DOM order so the first one focuses the first
    // invalid control on the step. Property type is the first property control.
    const propertyType = values.get(PROPERTY_TYPE_KEY).trim();
    const knownType = PROPERTY_TYPE_OPTIONS.some((o) => o.value === propertyType);
    if (!knownType) {
      errors.push({ field: PROPERTY_TYPE_KEY, message: "Select the property type to continue." });
    }

    const state = values.get(PROPERTY_STATE_KEY).trim();
    if (state === "") {
      errors.push({ field: PROPERTY_STATE_KEY, message: "Select the property state to continue." });
    } else if (!isValidStateCode(state)) {
      errors.push({
        field: PROPERTY_STATE_KEY,
        message: "Select a valid US state or territory for the property.",
      });
    }

    const zip = values.get(PROPERTY_ZIP_KEY).trim();
    if (zip !== "" && !PROPERTY_ZIP_REGEX.test(zip)) {
      errors.push({
        field: PROPERTY_ZIP_KEY,
        message: "Enter a 5-digit ZIP code, or leave it blank.",
      });
    }

    return errors;
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">
        Tell us about the property you're refinancing. The property type and property state
        are required — the street address, city, and ZIP code are optional.
      </p>
      ${selectFieldHtml(
        PROPERTY_TYPE_KEY,
        "Property type",
        "Select a property type",
        PROPERTY_TYPE_OPTIONS,
        values.get(PROPERTY_TYPE_KEY),
      )}
      ${textFieldHtml(PROPERTY_STREET_KEY, "Property street address", values.get(PROPERTY_STREET_KEY))}
      ${textFieldHtml(PROPERTY_CITY_KEY, "Property city", values.get(PROPERTY_CITY_KEY))}
      ${selectFieldHtml(
        PROPERTY_STATE_KEY,
        "Property state",
        "Select a property state",
        STATE_OPTIONS,
        values.get(PROPERTY_STATE_KEY),
      )}
      ${textFieldHtml(PROPERTY_ZIP_KEY, "Property ZIP code", values.get(PROPERTY_ZIP_KEY), {
        inputmode: "numeric",
        maxlength: 5,
      })}`;

    bindValueInputs(container, values);
  },
};

/**
 * Builds a required whole-dollar money step that consumes the reviewed
 * WI-TV-W65-002 shared money primitive (`bindWholeDollarMoneyInput`) without
 * forking, duplicating, or modifying it. Manual entry is whole dollars only —
 * `$###,###,###` formats live while the borrower types, pastes, deletes, or
 * edits, and any decimal/unsupported input is rejected at the input edge. A
 * trusted decimal prefill already accepted by the sanitizer stays exact in
 * wizard state and calculation while its display truncates toward zero, until
 * the first accepted manual whole-dollar edit replaces it. The canonical
 * numeric text and the existing numeric LMD mapping/serialization are unchanged.
 */
function wholeDollarMoneyStep(
  id: string,
  title: string,
  key: string,
  label: string,
  description: string,
  requiredMessage: string,
): WizardStep {
  return {
    id,
    title,
    validate: (values: WizardValues): WizardFieldError[] => {
      const raw = values.get(key);
      if (raw.trim() === "") return [{ field: key, message: requiredMessage }];
      const parsed = parseMoneyInput(raw);
      if (parsed === null) return [{ field: key, message: WHOLE_DOLLAR_FORMAT_MESSAGE }];
      if (!(parsed > 0)) {
        return [{ field: key, message: "Enter an amount greater than $0." }];
      }
      return [];
    },
    render: (container: HTMLElement, values: WizardValues): void => {
      container.innerHTML = `
        <p class="wizard-step-desc">${escapeHtml(description)}</p>
        ${textFieldHtml(key, label, formatWholeDollarForDisplay(values.get(key)), {
          required: true,
          inputmode: "numeric",
        })}`;

      const input = container.querySelector<HTMLInputElement>(`#${key}`);
      if (!input) return;
      // Consume the shared primitive: only canonical numeric text reaches the
      // value store, and the caret/composition/rejection behavior is the same
      // one Purchase already uses.
      bindWholeDollarMoneyInput(
        input,
        (canonical) => {
          values.set(key, canonical ?? "");
        },
        values.get(key),
      );
    },
  };
}

/** Step 3 — Estimated property value (required, > 0). */
export const estimatedValueStep = wholeDollarMoneyStep(
  "estimated-value",
  "Estimated property value",
  ESTIMATED_VALUE_KEY,
  "Estimated property value",
  "Your best estimate of what the property is worth today.",
  "Enter the estimated property value to continue.",
);

/** Step 4 — Current mortgage balance (required, > 0). */
export const currentBalanceStep = wholeDollarMoneyStep(
  "current-balance",
  "Current mortgage balance",
  CURRENT_LOAN_BALANCE_KEY,
  "Current mortgage balance",
  "How much you still owe on your current mortgage.",
  "Enter your current mortgage balance to continue.",
);

/**
 * Step 5 — Requested loan amount (required, > 0), applicable to cash-out only
 * (WI-TV-W65-006).
 *
 * Rate-and-term borrows exactly what they still owe, so the step does not exist
 * for that goal: it is not numbered, not reachable, not validated, and the
 * amount it owns is cleared rather than carried invisibly. Cash-out keeps the
 * separate required answer and uses the same shared whole-dollar input the
 * other money steps use.
 */
export const loanAmountStep: WizardStep = {
  ...wholeDollarMoneyStep(
    "loan-amount",
    "Requested loan amount",
    LOAN_AMOUNT_KEY,
    "Requested loan amount",
    "How much you'd like your new loan to be.",
    "Enter the loan amount you're requesting to continue.",
  ),
  valueKeys: [LOAN_AMOUNT_KEY],
  applies: (values: WizardValues): boolean => isCashOutRefinance(values.snapshot()),
};

/**
 * Step 10 — Email address. The follow-up sentence is the refinance one; the
 * required valid-address contract is the shared borrower-step contract.
 */
export const borrowerEmailStep = createBorrowerEmailStep(
  "We'll use this to follow up on your refinance.",
);

/**
 * Step 7 (VA Streamline) — Required military branch. Auto-asserts the
 * application-level military answer is "yes" and only exposes the branch
 * selector, because VA Streamline eligibility requires a military component.
 * This is a refinance-only variant of the shared military step; it only applies
 * when the goal is VA Streamline so the standard optional military step remains
 * available for every other goal.
 */
const vaStreamlineMilitaryStep: WizardStep = {
  id: "military-va-streamline",
  title: "Military service",
  validate: (values: WizardValues): WizardFieldError[] => {
    const component = values.get(MILITARY_COMPONENT_KEY).trim();
    if (MILITARY_COMPONENT_OPTIONS.some((o) => o.value === component)) return [];
    const firstId = `${MILITARY_COMPONENT_KEY}-${MILITARY_COMPONENT_OPTIONS[0].value.replace(/[^A-Za-z0-9]+/g, "-")}`;
    return [{ field: firstId, message: "Select your military branch to continue." }];
  },
  applies: (values: WizardValues): boolean => isVaStreamlineRefinance(values.snapshot()),
  render: (container: HTMLElement, values: WizardValues): void => {
    values.set(MILITARY_ANSWER_KEY, "yes");
    container.innerHTML = `
      <div class="wizard-conditional-group" role="group" aria-labelledby="military-component-desc">
        <p class="wizard-step-desc" id="military-component-desc">
          Which branch did you or your co-borrower serve in?
          <span class="wizard-req" aria-hidden="true">*</span>
        </p>
        <p class="wizard-hint" id="military-component-hint">Required to continue.</p>
        ${radioGroupHtml(
          MILITARY_COMPONENT_KEY,
          "Military branch",
          MILITARY_COMPONENT_OPTIONS,
          values.get(MILITARY_COMPONENT_KEY),
          { describedBy: "military-component-desc military-component-hint", autofocus: true },
        )}
      </div>`;
    bindRadioGroup(container, MILITARY_COMPONENT_KEY, values);
  },
};

/**
 * Standard Step 7 (optional/skippable) only applies when the goal is NOT
 * VA Streamline. It is the shared borrower-steps implementation, unchanged.
 */
const standardMilitaryStep: WizardStep = {
  ...militaryStep,
  applies: (values: WizardValues): boolean => !isVaStreamlineRefinance(values.snapshot()),
};

/**
 * The full locked refinance flow. Ordering and the required/optional (Skip)
 * contract match the Wave 5 locked flow. Steps 6 and 7 (credit, military
 * eligibility) are optional/skippable; every other step is required. Concrete
 */
export function createRefinanceSteps(): WizardStep[] {
  return [
    refinanceGoalStep,
    propertyDetailsStep,
    estimatedValueStep,
    currentBalanceStep,
    loanAmountStep,
    creditStep,
    standardMilitaryStep,
    vaStreamlineMilitaryStep,
    borrowerNameStep,
    borrowerPhoneStep,
    borrowerEmailStep,
    ...createCoBorrowerSteps(),
  ];
}
